import { hash } from '@ember/helper';
import { trustHTML } from '@ember/template';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';

import type { BarData } from '../../../types/components/prism/data-bar.ts';
import {
  COLOR_TRAFFIC_LIGHT_NEUTRAL,
  getCssVariableValue,
} from '../../../utils/css-variable.ts';
import PrismDataBarBlocksDataBarLabel from './blocks/data-bar-label.gts';
import PrismDataBarBlocksDataBarValue from './blocks/data-bar-value.gts';
import PrismDataBarDataBarBasic from './data-bar-basic.gts';
import styles from './data-bar-label-value-target.module.css';

function getStyleForDataBar(targetValue?: number): string {
  if (targetValue === undefined) {
    return '';
  }

  // Target marker is 3px wide
  let leftOffset = 3;

  // If it's too small it will appear before the data bar, so negate that
  if (targetValue < 3) {
    leftOffset = 0;
  }

  return `left: calc(${Math.min(targetValue, 100)}% - ${leftOffset}px);`;
}

type ValueItem = {
  isHidden: boolean;
  style: string;
  widthPercentage?: number;
};

interface PrismDataBarDataBarLabelValueTargetSignature {
  Args: {
    backgroundColor?: string;
    data: BarData[];
    direction?: 'regular' | 'reverse';
    isGradient?: boolean;
    isStriped?: boolean;
    rounded?: 'left' | 'right' | 'both';
    targetValue?: number;
    thresholdWidthPercentage?: number;
    valuePosition?: 'top' | 'bottom';
  };
  Blocks: {
    default: [
      {
        label?: WithBoundArgs<
          typeof PrismDataBarBlocksDataBarLabel,
          'color' | 'text' | 'tooltip'
        >;
        value?: WithBoundArgs<
          typeof PrismDataBarBlocksDataBarValue,
          'color' | 'text' | 'tooltip' | 'valueItems'
        >;
      },
    ];
  };
  Element: HTMLDivElement;
}

export default class PrismDataBarDataBarLabelValueTarget extends Component<PrismDataBarDataBarLabelValueTargetSignature> {
  get backgroundColor(): string {
    const { backgroundColor } = this.args;

    return backgroundColor ?? getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL);
  }

  get bottomValueItems(): ValueItem[] {
    const { data } = this.args;

    if (!data) {
      return [
        {
          isHidden: false,
          style: [
            `background-color: ${getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL)};`,
            'width: 0%;',
          ].join(' '),
          widthPercentage: undefined,
        },
      ];
    }

    return data
      .filter((datum) => {
        return (datum?.widthPercentage ?? 0) !== 0;
      })
      .map((datum) => {
        const widthPercentage = datum?.widthPercentage ?? 0;

        return {
          isHidden: Boolean(datum?.isHidden),
          style: `width: ${widthPercentage}%;`,
          widthPercentage: Math.round(widthPercentage),
        };
      });
  }

  get dataBarThresholdStyle(): string {
    const { thresholdWidthPercentage } = this.args;

    return `width: ${thresholdWidthPercentage ?? 100}%;`;
  }

  get showBottomValues(): boolean {
    return this.args.valuePosition === 'bottom';
  }

  get showTargetValue(): boolean {
    return this.args.targetValue !== undefined;
  }

  <template>
    <div class={{styles.container}}>
      <div class={{styles.top}}>
        <div class={{styles.top-label}}>
          {{! This div serves the purpose of making the value align to the right even when the label is not yielded.}}
          {{yield (hash label=(component PrismDataBarBlocksDataBarLabel))}}
        </div>

        <div data-test-id="data-bar-value">
          {{#unless this.showBottomValues}}
            {{yield (hash value=(component PrismDataBarBlocksDataBarValue))}}
          {{/unless}}
        </div>
      </div>

      <div class={{styles.content}}>
        <PrismDataBarDataBarBasic
          @backgroundColor={{@backgroundColor}}
          @data={{@data}}
          @dataBarThresholdStyle={{this.dataBarThresholdStyle}}
          @direction={{@direction}}
          @isGradient={{@isGradient}}
          @isStriped={{@isStriped}}
          @rounded={{@rounded}}
        />

        {{#if this.showTargetValue}}
          <div
            class={{styles.bar-target}}
            style={{trustHTML (getStyleForDataBar @targetValue)}}
          ></div>
        {{/if}}
      </div>

      {{#if this.showBottomValues}}
        <div
          data-test-id="data-bar-value"
          style={{trustHTML this.dataBarThresholdStyle}}
        >
          {{yield
            (hash
              value=(component
                PrismDataBarBlocksDataBarValue
                showBottomValues=this.showBottomValues
                valueItems=this.bottomValueItems
              )
            )
          }}
        </div>
      {{/if}}
    </div>
  </template>
}
